import { ComponentProps, FC, useMemo } from 'react';
import { ModalCloseIcon, modalLayoutTheme, Modal } from '@clds/modal';
import { useIntl } from 'react-intl';
import { messages } from '../../i18n/upgradeModalMessages';
import styled from 'styled-components';
import { typography } from '@clds/typography';

import { UpgradeButtons } from './UpgradeButtons';
import { UpgradeHeader } from './UpgradeHeader';
import { UpgradeImage } from './UpgradeImage';

const CHECK_IMG = 'https://res.cloudinary.com/prod/image/upload/upgrade-popup-modal/check.png';

const Title = styled.div`
  ${typography.md.highContrast};
  font-weight: 600;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const CheckIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const ContentContainer = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.sm};
`;

const ContentNote = styled.div`
  ${typography.md.highContrast};
  font-weight: 400;
`;

const ModelContainer = styled.div`
  display: flex;
  width: 818px;
  background-color: ${modalLayoutTheme.layout.content.background.color};
  border-radius: ${(props) => props.theme.radius.lg};
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: ${(props) => props.theme.spacing.xl} ${(props) => props.theme.spacing.xl};
  flex: 1;
`;

const RightContainer = styled.div`
  position: relative;
`;

const CloseIcon = styled(ModalCloseIcon)`
  position: absolute;
  top: 0;
  right: 0;
  transform: none;
`;
interface ModalAnalytics {
  category: string;
  label: string;
  action: string;
}

type ModalProps = ComponentProps<typeof Modal>;
export type CloseTrigger = 'x button' | 'close button';
export type UpgradePopupModalProps = Pick<ModalProps, 'open'> & {
  onClose: (reason: CloseTrigger) => void;
  modelPopupKind: 'addProductEnvModal' | 'addUserModal';
};

export const UpgradePopupModal: FC<UpgradePopupModalProps> = ({ onClose, modelPopupKind, ...rest }) => {
  const { formatMessage } = useIntl();

  let upgradeOptionsAnalytics: ModalAnalytics = { category: '', label: '', action: '' };
  let accountManagementAnalytics: ModalAnalytics = { category: '', label: '', action: '' };

  if (modelPopupKind === 'addProductEnvModal') {
    upgradeOptionsAnalytics = { category: 'side menu', label: 'add prod env upgrade modal | upgrade options', action: 'navigate' };
    accountManagementAnalytics = { category: 'side menu', label: 'add prod env upgrade modal | account settings', action: 'navigate' };
  }
  if (modelPopupKind === 'addUserModal') {
    upgradeOptionsAnalytics = { category: 'primary menu', label: 'new user upgrade modal | upgrade options', action: 'navigate' };
    accountManagementAnalytics = { category: 'primary menu', label: 'new user upgrade modal | user management settings', action: 'navigate' };
  }

  const contentMessages = useMemo(
    () => [
      formatMessage(messages.additionalUsers),
      formatMessage(messages.moreProductEnvironments),
      formatMessage(messages.higherUsageLimits),
      formatMessage(messages.backup),
      formatMessage(messages.access),
      formatMessage(messages.errorReports),
      formatMessage(messages.expeditedSupport),
    ],
    [formatMessage]
  );

  const content = useMemo(
    () =>
      contentMessages.map((message) => {
        return (
          <ContentContainer key={message}>
            <CheckIcon src={CHECK_IMG}></CheckIcon>
            <ContentNote>{message}</ContentNote>
          </ContentContainer>
        );
      }),
    [contentMessages]
  );

  return (
    <Modal {...rest} onClose={() => onClose('x button')}>
      <ModelContainer>
        <LeftContainer>
          <UpgradeHeader modelPopupKind={modelPopupKind}></UpgradeHeader>
          <Title>{formatMessage(messages.title)}</Title>
          {content}
          <UpgradeButtons
            upgradeOptionsAnalytics={upgradeOptionsAnalytics}
            accountManagementAnalytics={accountManagementAnalytics}
            modelPopupKind={modelPopupKind}
          />
        </LeftContainer>
        <RightContainer>
          <CloseIcon />
          <UpgradeImage modelPopupKind={modelPopupKind}></UpgradeImage>
        </RightContainer>
      </ModelContainer>
    </Modal>
  );
};
